import { useCallback, useMemo, useRef } from 'react';
import { useUnmount, useMount } from 'react-use';
import { throttle } from 'lodash';
import { useEventListener } from 'taboola-ultimate-ui';
import { gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { useAppEventsApi } from 'services/api/appEventsApi/useAppEventsApi';
import { authTokenManager } from '../../authentication';

export const REMOTE_PUSH_TIMEOUT = 250;
export const QUEUE_SIZE_THRESHOLD = 50;
export const MAX_RETRIES = 3;
export const RETRY_DELAY = 500;

export const useAppEventQueueRelease = (register, throttleTimeout = REMOTE_PUSH_TIMEOUT) => {
    const queue = useRef([]);
    const { sendEventList } = useAppEventsApi();

    const pushToQueue = useCallback(event => {
        queue.current.push(event);
    }, []);

    const setQueue = useCallback(newQueue => {
        queue.current = newQueue;
    }, []);

    const release = useCallback(async () => {
        if (!authTokenManager.getToken()) {
            return;
        }
        const length = queue.current.length;
        if (!length) {
            return;
        }

        const listToSend = queue.current.slice(0, length);
        queue.current = queue.current.slice(length);

        for (let i = 0; i < MAX_RETRIES; i++) {
            try {
                await sendEventList(listToSend);
                break;
            } catch (error) {
                if (i === MAX_RETRIES - 1) {
                    gtmTracker.trackError(`Failed to send custom events with error: ${error}`);
                }
            }

            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
    }, [sendEventList, queue]);

    const throttledRelease = useMemo(
        () => throttle(release, throttleTimeout, { leading: false }),
        [release, throttleTimeout]
    );

    useMount(
        register(() => {
            if (queue.current.length > QUEUE_SIZE_THRESHOLD) {
                release();
                return;
            }
            throttledRelease.cancel();
            throttledRelease();
        })
    );

    // On hiding tab we do instant release
    useEventListener(
        'visibilitychange',
        () => {
            if (document.hidden) {
                release();
            }
        },
        document
    );

    useUnmount(release);

    return { pushToQueue, setQueue, queue };
};
